import { useState } from "react";
import { ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { AtwProductMark } from "@/components/Brand/AtwProductMark";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import type { GlobalAppSlug } from "@/lib/dashboardAppRegistry";
import type { AtwStoredSystemId } from "@/lib/atwSystemAccess";
import {
  ACCESS_ACTIONS,
  ACCESS_CATALOG,
  ACCESS_TEMPLATES,
  accessTemplate,
  moduleActionAllowed,
  moduleId,
  sanitizeModuleOverrides,
  type AccessAction,
  type AccessApplication,
  type AccessSystem,
  type AccessTemplateId,
  type ModuleOverrides,
} from "@/lib/accessCatalog";

const markToneClass = "dark:brightness-0 dark:invert";
const ACTION_LABEL: Record<AccessAction, string> = {
  view: "View",
  create: "Create",
  edit: "Edit",
  delete: "Delete",
};

export type AccessDraft = {
  systemAccess: AtwStoredSystemId[];
  /** Explicit app entitlements (no `home`). */
  apps: Exclude<GlobalAppSlug, "home">[];
  templateId: AccessTemplateId;
  overrides: ModuleOverrides;
};

type Props = {
  value: AccessDraft;
  onChange?: (next: AccessDraft) => void;
  /** Apps this organization may grant. */
  assignableAppSlugs: Exclude<GlobalAppSlug, "home">[];
  /** Owners and super admins: every app and every action. */
  fullAccessLocked?: boolean;
  /** Organization has ATW Mail turned on. */
  mailAvailable?: boolean;
  readOnly?: boolean;
};

/**
 * System → Application → Module → View / Create / Edit / Delete.
 * Systems and apps switch on/off; module actions come from the template plus per-person overrides.
 */
export function AccessTreeEditor({
  value,
  onChange,
  assignableAppSlugs,
  fullAccessLocked,
  mailAvailable = true,
  readOnly,
}: Props) {
  const editable = !readOnly && Boolean(onChange);
  const assignable = new Set<string>(assignableAppSlugs);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const overrideCount = Object.values(value.overrides).reduce((n, g) => n + Object.keys(g).length, 0);

  const emit = (patch: Partial<AccessDraft>) => onChange?.({ ...value, ...patch });

  const systemOn = (s: AccessSystem) => s.pinned === true || value.systemAccess.includes(s.id as AtwStoredSystemId);
  const appOn = (app: AccessApplication) => !app.entitlement || fullAccessLocked || value.apps.includes(app.entitlement);

  const setSystem = (s: AccessSystem, on: boolean) => {
    if (s.pinned || s.id === "directory") return;
    const id = s.id;
    const next = new Set(value.systemAccess);
    if (on) next.add(id);
    else next.delete(id);
    emit({ systemAccess: [...next].sort() as AtwStoredSystemId[] });
  };
  const setApp = (app: AccessApplication, on: boolean) => {
    if (!app.entitlement) return;
    const next = new Set(value.apps);
    if (on) next.add(app.entitlement);
    else next.delete(app.entitlement);
    emit({ apps: [...next].sort() as Exclude<GlobalAppSlug, "home">[] });
  };
  const setAction = (app: AccessApplication, moduleKey: string, action: AccessAction, on: boolean) => {
    const id = moduleId(app.key, moduleKey);
    const next: ModuleOverrides = { ...value.overrides, [id]: { ...(value.overrides[id] || {}), [action]: on } };
    emit({ overrides: sanitizeModuleOverrides(value.templateId, next) });
  };

  const visibleApps = (s: AccessSystem) =>
    s.applications.filter((a) => !a.entitlement || assignable.has(a.entitlement));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Access template</p>
          {fullAccessLocked ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Organization owners and super admins have every app and every action.
            </p>
          ) : (
            <>
              <select
                value={value.templateId}
                disabled={!editable}
                onChange={(event) => {
                  const id = event.target.value as AccessTemplateId;
                  emit({
                    templateId: id,
                    overrides: sanitizeModuleOverrides(id, value.overrides),
                  });
                }}
                className="h-10 w-full max-w-xs rounded-md border border-gray-300 bg-white px-3 text-sm dark:border-white/20 dark:bg-[#12161d] dark:text-white"
                aria-label="Access template"
              >
                {ACCESS_TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400">{accessTemplate(value.templateId).summary}</p>
            </>
          )}
        </div>
        {!fullAccessLocked && overrideCount > 0 ? (
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {overrideCount} change{overrideCount === 1 ? "" : "s"} from template
            </Badge>
            {editable ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => emit({ overrides: {} })}>
                <RotateCcw className="mr-1.5 size-3.5" /> Reset
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <ul className="space-y-3">
        {ACCESS_CATALOG.map((system) => {
          const sOn = systemOn(system);
          const unavailable = system.id === "workspaces" && !mailAvailable;
          const apps = visibleApps(system);
          return (
            <li
              key={system.id}
              className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-[#1D232D]"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                  <AtwProductMark id={system.mark} size={36} className={markToneClass} label />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white">{system.name}</p>
                    <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                      {unavailable
                        ? "This organization doesn't have ATW Mail (WorkSuite) turned on."
                        : system.summary}
                    </p>
                  </div>
                </div>
                {editable && !unavailable && !system.pinned ? (
                  <Switch
                    aria-label={`${system.name} access`}
                    checked={sOn}
                    onCheckedChange={(on) => setSystem(system, on === true)}
                  />
                ) : (
                  <Badge variant={sOn ? "default" : "outline"}>{sOn ? "Can open" : "No access"}</Badge>
                )}
              </div>

              {sOn && apps.length > 0 ? (
                <div className="mt-3 space-y-2 border-t border-gray-100 pt-3 dark:border-white/10">
                  {apps.map((app) => {
                    const aOn = appOn(app);
                    const key = `${system.id}:${app.key}`;
                    const single = !app.entitlement;
                    // Start expanded so modules are visible; user can collapse.
                    const expanded = single || open[key] !== false;
                    return (
                      <div key={app.key} className="rounded-md border border-gray-100 dark:border-white/10">
                        {!single ? (
                          <div className="flex items-center justify-between gap-3 px-3 py-2">
                            <button
                              type="button"
                              onClick={() => setOpen({ ...open, [key]: !expanded })}
                              className="flex min-w-0 items-center gap-2 text-left text-sm font-medium text-gray-900 dark:text-white"
                              aria-expanded={expanded}
                              disabled={!aOn}
                            >
                              {aOn ? (
                                expanded ? (
                                  <ChevronDown className="size-4 shrink-0" />
                                ) : (
                                  <ChevronRight className="size-4 shrink-0" />
                                )
                              ) : (
                                <span className="w-4" />
                              )}
                              {app.mark ? <AtwProductMark id={app.mark} size={22} className={markToneClass} /> : null}
                              {app.name}
                            </button>
                            {editable && !fullAccessLocked ? (
                              <Switch
                                aria-label={`${app.name} access`}
                                checked={aOn}
                                onCheckedChange={(on) => setApp(app, on === true)}
                              />
                            ) : (
                              <span className={`text-xs ${aOn ? "text-emerald-700 dark:text-emerald-300" : "text-gray-400"}`}>
                                {aOn ? "Yes" : "No"}
                              </span>
                            )}
                          </div>
                        ) : null}

                        {aOn && expanded ? (
                          <div className="overflow-x-auto border-t border-gray-100 px-3 py-2 dark:border-white/10">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                  <th className="py-1 text-left font-medium">Module</th>
                                  {ACCESS_ACTIONS.map((a) => (
                                    <th key={a} className="w-20 py-1 text-center font-medium">
                                      {ACTION_LABEL[a]}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {app.modules.map((m) => (
                                  <tr key={m.key} className="border-t border-gray-50 dark:border-white/5">
                                    <td className="py-1.5 pr-3 text-gray-800 dark:text-gray-200">{m.name}</td>
                                    {ACCESS_ACTIONS.map((a) => {
                                      if (!m.actions.includes(a)) {
                                        return (
                                          <td key={a} className="text-center text-gray-300 dark:text-gray-600">
                                            —
                                          </td>
                                        );
                                      }
                                      const allowed =
                                        fullAccessLocked ||
                                        moduleActionAllowed(value.templateId, value.overrides, app.key, m.key, a);
                                      const changed = typeof value.overrides[moduleId(app.key, m.key)]?.[a] === "boolean";
                                      return (
                                        <td key={a} className="text-center">
                                          <span className="relative inline-flex items-center justify-center">
                                            <Checkbox
                                              checked={allowed}
                                              disabled={!editable || fullAccessLocked}
                                              onCheckedChange={(on) => setAction(app, m.key, a, on === true)}
                                              aria-label={`${app.name} ${m.name}: ${m.actionLabels?.[a] ?? ACTION_LABEL[a]}`}
                                              title={m.actionLabels?.[a] ?? ACTION_LABEL[a]}
                                            />
                                            {changed ? (
                                              <span
                                                className="absolute -right-1.5 -top-1.5 size-2 rounded-full bg-amber-500"
                                                title="Changed from template"
                                              />
                                            ) : null}
                                          </span>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        An amber dot marks a change from the template. Module permissions are enforced app by app as each app adopts them.
      </p>
    </div>
  );
}
